import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';

const segData = [
  /* 0 — Government */
  {
    text: 'Launch open data portals your citizens will love — in minutes. AI generates metadata automatically. Users explore datasets in plain English. Fully managed, no DevOps required. Trusted by 100+ governments and institutions worldwide. WCAG-compliant, DCAT-ready.',
    feats: [
      ['5-minute setup, fully managed', 'No DevOps, no IT ticket. Live before the meeting ends.'],
      ['DCAT-compatible, SEO-optimised', 'Discoverable, compliant, and accessible out of the box. WCAG 2.2 AA included.'],
      ['AI-generated metadata', 'Accelerate publishing and reduce form-filling. Upload a file — AI writes the metadata.'],
      ['CKAN-compatible APIs + seamless migration', 'Works with what you already have. Plug-and-play migration from existing CKAN instances.'],
    ],
  },
  /* 1 — Research */
  {
    text: "Give researchers a data repository they'll actually use. Launches in minutes, generates metadata automatically, and lets anyone explore datasets in plain English — no technical skills needed. FAIR-ready, DCAT-compliant, fully managed. Free 14-day trial.",
    feats: [
      ['AI-generated metadata', 'Researchers publish faster and fill fewer forms. Upload a dataset, metadata is done.'],
      ['DCAT / FAIR compatibility', 'Meets funder and institutional requirements out of the box. No configuration required.'],
      ['Natural language dataset exploration', 'Anyone can explore data without SQL or technical skills. Powered by Queryless (add-on).'],
      ['Publish visualisations and dashboards', 'Turn your repository into an insights catalog, not just a dataset registry.'],
    ],
  },
  /* 2 — Enterprise */
  {
    text: 'Stop emailing spreadsheets. PortalJS Cloud gives your team a beautiful internal data portal — live in 5 minutes, fully managed, with AI-generated metadata and AI-powered plain-English data exploration. No DevOps, no IT ticket. Your data, finally findable.',
    feats: [
      ['5-minute setup, no DevOps', 'Live before you finish your coffee. No infrastructure, no IT ticket, no project.'],
      ['Natural language exploration + AI charts', 'Your whole team can query data and get instant charts. Powered by Queryless.'],
      ['One unified search across all sources', 'Connect CKAN, Socrata, ArcGIS Hub, Dataverse — one catalog, one search.'],
      ['Publish visualisations and dashboards', 'Turn your data portal into an insights catalog — not just a place to store files.'],
    ],
  },
];

const SEG_LABELS = ['Government', 'Research', 'Enterprise'];

export default function PortalJSCloudSection() {
  const [active, setActive] = useState(0);
  const seg = segData[active];

  return (
    <section className="pjs-section" aria-label="PortalJS Cloud">
      <div className="pjs-inner">

        {/* ── LEFT: COPY ── */}
        <div className="pjs-copy">

          <div className="pjs-eyebrow">PortalJS Cloud</div>

          <h2 className="pjs-headline">
            Launch data portals your users actually love — in minutes.
          </h2>

          {/* Segment switcher */}
          <div className="pjs-seg-tabs" role="tablist" aria-label="Select your organisation type">
            {SEG_LABELS.map((label, i) => (
              <button
                key={label}
                className={`pjs-seg-tab${active === i ? ' active' : ''}`}
                role="tab"
                aria-selected={active === i}
                onClick={() => setActive(i)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Description */}
          <div className="pjs-seg-body">
            <p className="pjs-seg-text">{seg.text}</p>
          </div>

          {/* Feature list */}
          <div className="pjs-feats">
            {seg.feats.map(([title, desc]) => (
              <div key={title} className="pjs-feat">
                <div className="pjs-feat-dot" aria-hidden="true" />
                <p className="pjs-feat-copy">
                  <strong>{title}</strong> — {desc}
                </p>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="pjs-btns">
            <Link href="https://portaljs.com" target="_blank" rel="noopener noreferrer" className="btn-primary pjs-btn-primary">
              Try free →
            </Link>
            <Link href="https://portaljs.com/pricing" target="_blank" rel="noopener noreferrer" className="btn-secondary pjs-btn-secondary">
              See pricing
            </Link>
          </div>

          {/* Pricing tags */}
          <div className="pjs-tags">
            <span className="pjs-tag pjs-tag-free">Free OSS</span>
            <span className="pjs-tag pjs-tag-plan">$99/mo Foundation</span>
            <span className="pjs-tag pjs-tag-plan">$299/mo Institution</span>
          </div>

          {/* Trial callout */}
          <div className="pjs-trial-callout">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <circle cx="7" cy="7" r="6" stroke="#e07a28" strokeWidth="1.5" />
              <path d="M4.5 7l1.8 1.8 3.2-3.6" stroke="#e07a28" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            14-day free trial — no credit card required
          </div>

        </div>{/* /pjs-copy */}


        {/* ── RIGHT: DEVICE MOCKUP ── */}
        <div className="pjs-visual" aria-hidden="true">

          {/* Annotation: top-right */}
          <div className="pjs-ann pjs-ann-top-right">
            <div className="pjs-ann-bubble">
              AI metadata
              <small>Auto-generated on upload</small>
            </div>
            <div className="pjs-ann-line" />
            <div className="pjs-ann-dot" />
          </div>

          {/* Annotation: mid-right */}
          <div className="pjs-ann pjs-ann-mid-right">
            <div className="pjs-ann-bubble">
              Natural language query
              <small>No SQL required</small>
            </div>
            <div className="pjs-ann-line" />
            <div className="pjs-ann-dot" />
          </div>

          {/* Annotation: bottom-left */}
          <div className="pjs-ann pjs-ann-bot-left">
            <div className="pjs-ann-bubble">
              Open source core
              <small>No vendor lock-in</small>
            </div>
            <div className="pjs-ann-line" />
            <div className="pjs-ann-dot" />
          </div>

          <div className="pjs-device-wrap">
            <div className="pjs-device-outer">
              <div className="pjs-device-inner">

                {/* Menu bar */}
                <div className="pjs-dev-menubar">
                  <div className="pjs-traffic-light pjs-tl-red" />
                  <div className="pjs-traffic-light pjs-tl-amber" />
                  <div className="pjs-traffic-light pjs-tl-green" />
                  <div className="pjs-dev-urlbar">
                    <div className="pjs-dev-urlbar-dot" />
                    <span className="pjs-dev-urlbar-domain">portal.example.gov</span>
                    &nbsp;·&nbsp; Powered by PortalJS Cloud
                  </div>
                </div>

                {/* Badge bar */}
                <div className="pjs-dev-badgebar">
                  <span className="pjs-dev-badge pjs-db-green">● WCAG 2.2 AA</span>
                  <span className="pjs-dev-badge pjs-db-blue">DCAT-ready</span>
                  <span className="pjs-dev-badge pjs-db-orange">SEO-optimised</span>
                </div>

                {/* Screenshot */}
                <div className="pjs-dev-screenshot">
                  <Image
                    src="/assets/images/portaljjs.png"
                    alt="PortalJS Cloud — citizen-facing data portal screenshot"
                    width={1038}
                    height={750}
                    loading="lazy"
                    className="pjs-screenshot-img bypass-filter"
                  />
                </div>

                {/* Footer bar */}
                <div className="pjs-dev-footer">
                  <span className="pjs-dev-footer-text">Citizen-facing data portal · Search, browse and share open datasets</span>
                  <span className="pjs-dev-live">● Live in 5 min</span>
                </div>

              </div>{/* /pjs-device-inner */}
            </div>{/* /pjs-device-outer */}
          </div>{/* /pjs-device-wrap */}

        </div>{/* /pjs-visual */}

      </div>{/* /pjs-inner */}
    </section>
  );
}
